import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Trophy, RefreshCw, Star, Timer, Bird } from 'lucide-react';
import playerManager from '../../utils/playerManager';

const FlappyTap = ({ onGameComplete, gameConfig }) => {
  const [gameState, setGameState] = useState('waiting'); // waiting, playing, finished
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [timeLeft, setTimeLeft] = useState(gameConfig?.duration || 60);
  const [startTime, setStartTime] = useState(null);
  const [isInvincible, setIsInvincible] = useState(false);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const livesRef = useRef(3); // Track lives in ref to avoid state update delays
  const gameDataRef = useRef({
    bird: { x: 80, y: 250, velocity: 0, radius: 20 },
    pipes: [],
    pipeGap: 180,
    pipeWidth: 60,
    gravity: 0.6,
    jumpStrength: -10,
    pipeSpeed: 3,
    frameCount: 0
  });

  const CANVAS_WIDTH = 400;
  const CANVAS_HEIGHT = 600;

  // Game timer
  useEffect(() => {
    if (gameState === 'playing' && timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft(time => time - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (gameState === 'playing' && timeLeft === 0) {
      endGame();
    }
  }, [gameState, timeLeft]);

  useEffect(() => {
    if (gameState === 'playing') {
      startGame();
    }
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [gameState]);

  const startGame = () => {
    const data = gameDataRef.current;
    
    // Reset game data
    data.bird = { x: 80, y: 250, velocity: 0, radius: 20 };
    data.pipes = [];
    data.frameCount = 0;
    setScore(0);
    setLives(3);
    livesRef.current = 3; // Reset lives ref
    setIsInvincible(false);
    setTimeLeft(gameConfig?.duration || 60);
    setStartTime(Date.now());

    // Start game loop
    animationRef.current = requestAnimationFrame(gameLoop);
  };

  const gameLoop = () => {
    update();
    draw();
    
    if (gameState === 'playing') {
      animationRef.current = requestAnimationFrame(gameLoop);
    }
  };

  const update = () => {
    const data = gameDataRef.current;
    
    // Update bird
    data.bird.velocity += data.gravity;
    data.bird.y += data.bird.velocity;

    // Spawn pipes
    data.frameCount++;
    if (data.frameCount % 90 === 0) {
      const gapY = Math.random() * (CANVAS_HEIGHT - data.pipeGap - 200) + 100;
      data.pipes.push({
        x: CANVAS_WIDTH,
        topHeight: gapY,
        bottomY: gapY + data.pipeGap,
        passed: false
      });
    }

    // Update pipes
    data.pipes.forEach((pipe, index) => {
      pipe.x -= data.pipeSpeed;

      // Score when passing pipe
      if (!pipe.passed && pipe.x + data.pipeWidth < data.bird.x) {
        pipe.passed = true;
        setScore(prev => {
          const newScore = prev + 1;
          if (newScore > highScore) {
            setHighScore(newScore);
          }
          return newScore;
        });
      }

      // Remove off-screen pipes
      if (pipe.x + data.pipeWidth < 0) {
        data.pipes.splice(index, 1);
      }
    });

    // Check collisions (only if not invincible)
    if (!isInvincible && (checkCollision() || data.bird.y + data.bird.radius > CANVAS_HEIGHT || data.bird.y - data.bird.radius < 0)) {
      handleCollision();
    }
  };

  const handleCollision = () => {
    // Use ref to avoid state update delays
    const newLives = livesRef.current - 1;
    livesRef.current = newLives;
    setLives(newLives);
    
    console.log('Collision! Lives remaining:', newLives);
    
    if (newLives <= 0) {
      // Game over - no lives left
      endGame();
    } else {
      // Reset bird position but keep playing
      const data = gameDataRef.current;
      data.bird.y = 250;
      data.bird.velocity = 0;
      
      // Make invincible for 2 seconds
      setIsInvincible(true);
      setTimeout(() => {
        setIsInvincible(false);
      }, 2000);
    }
  };

  const checkCollision = () => {
    const data = gameDataRef.current;
    const bird = data.bird;

    for (let pipe of data.pipes) {
      // Check if bird is in pipe's x range
      if (
        bird.x + bird.radius > pipe.x &&
        bird.x - bird.radius < pipe.x + data.pipeWidth
      ) {
        // Check if bird hits top or bottom pipe
        if (
          bird.y - bird.radius < pipe.topHeight ||
          bird.y + bird.radius > pipe.bottomY
        ) {
          return true;
        }
      }
    }
    return false;
  };

  const draw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const data = gameDataRef.current;

    // Clear canvas with gradient
    const gradient = ctx.createLinearGradient(0, 0, 0, CANVAS_HEIGHT);
    gradient.addColorStop(0, '#87CEEB');
    gradient.addColorStop(1, '#E0F6FF');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // Draw clouds
    drawClouds(ctx);

    // Draw pipes
    ctx.fillStyle = '#2ECC71';
    data.pipes.forEach(pipe => {
      // Top pipe
      ctx.fillRect(pipe.x, 0, data.pipeWidth, pipe.topHeight);
      
      // Pipe cap (top)
      ctx.fillStyle = '#27AE60';
      ctx.fillRect(pipe.x - 5, pipe.topHeight - 30, data.pipeWidth + 10, 30);
      
      // Bottom pipe
      ctx.fillStyle = '#2ECC71';
      ctx.fillRect(pipe.x, pipe.bottomY, data.pipeWidth, CANVAS_HEIGHT - pipe.bottomY);
      
      // Pipe cap (bottom)
      ctx.fillStyle = '#27AE60';
      ctx.fillRect(pipe.x - 5, pipe.bottomY, data.pipeWidth + 10, 30);
    });

    // Draw bird
    drawBird(ctx, data.bird);

    // Draw score
    ctx.fillStyle = 'white';
    ctx.strokeStyle = 'black';
    ctx.lineWidth = 3;
    ctx.font = 'bold 40px Arial';
    ctx.textAlign = 'center';
    ctx.strokeText(score, CANVAS_WIDTH / 2, 60);
    ctx.fillText(score, CANVAS_WIDTH / 2, 60);
  };

  const drawClouds = (ctx) => {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
    
    // Cloud 1
    ctx.beginPath();
    ctx.arc(100, 100, 30, 0, Math.PI * 2);
    ctx.arc(130, 100, 40, 0, Math.PI * 2);
    ctx.arc(160, 100, 30, 0, Math.PI * 2);
    ctx.fill();

    // Cloud 2
    ctx.beginPath();
    ctx.arc(300, 150, 25, 0, Math.PI * 2);
    ctx.arc(320, 150, 35, 0, Math.PI * 2);
    ctx.arc(345, 150, 25, 0, Math.PI * 2);
    ctx.fill();
  };

  const drawBird = (ctx, bird) => {
    // Bird body
    ctx.fillStyle = '#FFD700';
    ctx.beginPath();
    ctx.arc(bird.x, bird.y, bird.radius, 0, Math.PI * 2);
    ctx.fill();

    // Bird outline
    ctx.strokeStyle = '#FFA500';
    ctx.lineWidth = 3;
    ctx.stroke();

    // Eye
    ctx.fillStyle = 'white';
    ctx.beginPath();
    ctx.arc(bird.x + 8, bird.y - 5, 6, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = 'black';
    ctx.beginPath();
    ctx.arc(bird.x + 10, bird.y - 5, 3, 0, Math.PI * 2);
    ctx.fill();

    // Beak
    ctx.fillStyle = '#FF6347';
    ctx.beginPath();
    ctx.moveTo(bird.x + bird.radius, bird.y);
    ctx.lineTo(bird.x + bird.radius + 15, bird.y - 5);
    ctx.lineTo(bird.x + bird.radius + 15, bird.y + 5);
    ctx.closePath();
    ctx.fill();

    // Wing
    ctx.fillStyle = '#FFA500';
    ctx.beginPath();
    ctx.ellipse(bird.x - 5, bird.y + 5, 15, 10, Math.PI / 4, 0, Math.PI * 2);
    ctx.fill();
  };

  const handleTap = () => {
    if (gameState !== 'playing') return;
    
    const data = gameDataRef.current;
    data.bird.velocity = data.jumpStrength;
  };

  const endGame = async () => {
    setGameState('finished');
    if (animationRef.current) cancelAnimationFrame(animationRef.current);
    
    const completionTime = startTime ? Date.now() - startTime : 0;
    const gameData = {
      pipesPassed: score,
      finalScore: score,
      completion_time: completionTime
    };

    // Update high score
    if (score > highScore) {
      setHighScore(score);
    }

    // Submit score to backend
    try {
      await playerManager.submitScore('flappy-tap', 'Flappy Tap Challenge', score, gameData);
    } catch (error) {
      console.error('Failed to submit score:', error);
    }

    setTimeout(() => {
      onGameComplete(score, gameData);
    }, 3000);
  };

  const restartGame = () => {
    setGameState('playing');
  };

  // Waiting state
  if (gameState === 'waiting') {
    return (
      <div className="text-center">
        <Card className="gaming-card max-w-lg mx-auto p-8">
          <CardHeader className="pb-6">
            <div className="text-6xl mb-4">🐦</div>
            <CardTitle className="text-2xl text-white">Flappy Tap Challenge</CardTitle>
            <p className="text-gray-400">
              Tap to keep the bird flying! You have 3 lives (❤️❤️❤️). Navigate through pipes and score points!
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 mb-6">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Duration:</span>
                <span className="text-white">{gameConfig?.duration || 60} seconds</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Entry Fee:</span>
                <span className="text-yellow-400 font-semibold">₹10</span>
              </div>
            </div>
            <Button onClick={() => setGameState('playing')} className="w-full btn-primary py-3 text-lg">
              Start Flying
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Finished state
  if (gameState === 'finished') {
    return (
      <div className="text-center">
        <Card className="gaming-card max-w-lg mx-auto p-8">
          <CardHeader className="pb-6">
            <Trophy className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            <CardTitle className="text-2xl text-white">Game Complete!</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 mb-6">
              <div className="text-center">
                <div className="text-4xl font-bold text-yellow-400 mb-2">{score}</div>
                <div className="text-gray-400">Final Score</div>
              </div>
              
              {score === highScore && score > 0 && (
                <div className="text-lg text-green-400">🎉 New High Score!</div>
              )}
              
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-blue-400">{score}</div>
                  <div className="text-gray-400 text-sm">Pipes Passed</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-purple-400">{highScore}</div>
                  <div className="text-gray-400 text-sm">High Score</div>
                </div>
              </div>
            </div>
            
            <div className="text-gray-400 text-sm">
              Returning to games lobby in 3 seconds...
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4">
      {/* Game HUD */}
      <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
        <div className="flex flex-wrap gap-2">
          <div className="bg-yellow-400/20 text-yellow-400 border border-yellow-400/30 px-3 py-1 rounded-md text-sm">
            Score: {score}
          </div>
          <div className={`${isInvincible ? 'bg-green-500/20 text-green-400 border-green-500/30 animate-pulse' : 'bg-red-500/20 text-red-400 border-red-500/30'} border px-3 py-1 rounded-md flex items-center space-x-1 text-sm`}>
            <span>{'❤️'.repeat(lives)}</span>
          </div>
          <div className="bg-purple-500/20 text-purple-400 border border-purple-500/30 px-3 py-1 rounded-md flex items-center space-x-1 text-sm">
            <Star className="w-3 h-3" />
            <span>{highScore}</span>
          </div>
        </div>
        <div className="bg-blue-500/20 text-blue-400 border border-blue-500/30 px-3 py-1 rounded-md flex items-center space-x-1 text-sm">
          <Timer className="w-3 h-3" />
          <span>{timeLeft}s</span>
        </div>
      </div>

      {/* Game Canvas */}
      <Card className="gaming-card p-2 overflow-hidden">
        <canvas
          ref={canvasRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          className="border-4 border-gray-700 rounded-lg w-full h-auto cursor-pointer shadow-2xl"
          style={{ maxWidth: '100%', height: 'auto', aspectRatio: '2/3' }}
          onClick={handleTap}
        />
      </Card>

      {/* Instructions */}
      <div className="mt-4 text-center text-gray-400 text-xs md:text-sm px-2">
        👆 Tap anywhere to fly up | Avoid the green pipes!
      </div>
    </div>
  );
};

export default FlappyTap;
